import { MIXED_TASK_COLOR, MIXED_TASK_STYLE, STRIPED_TASK_STYLE } from "../constants/TaskTypes.jsx";
import { SWIVU_TASK_DEFAULT_COLOR } from "../configs/globalConfig.jsx";
import { applyStripedBackground } from "./utils.jsx";
import { ViewType } from "../constants/OtherKeys.jsx";

export default function bgContrastHelper(bgColor, lightColor, darkColor) {
  let L = 1;
  if (bgColor) {
    const color = (bgColor.charAt(0) === '#') ? bgColor.substring(1, 7) : bgColor;
    const r = parseInt(color.substring(0, 2), 16); // hexToR
    const g = parseInt(color.substring(2, 4), 16); // hexToG
    const b = parseInt(color.substring(4, 6), 16); // hexToB
    const uicolors = [r / 255, g / 255, b / 255];
    const c = uicolors.map((col) => {
      if (col <= 0.03928) {
        return col / 12.92;
      }
      return Math.pow((col + 0.055) / 1.055, 2.4);
    });
    L = (0.2126 * c[0]) + (0.7152 * c[1]) + (0.0722 * c[2]);
  }
  return (L > 0.179) ? darkColor : lightColor;
}

export const getNewTaskColor = (TaskEditorColorMapping, excludedColors = []) => {
  // Step 1: Filter out colors that are in the excludedColors array
  const availableColors = TaskEditorColorMapping.filter((colorInfo) => {
    return !excludedColors.includes(colorInfo.color);
  });

  // Step 2: Calculate the hue, saturation, and lightness of the excludedColors
  const excludedColorsHSL = excludedColors.map((color) => {
    return hexToHSL(color);
  });

  // Step 3: Find a color that is not similar to the excludedColors
  return findDissimilarColor(availableColors, excludedColorsHSL);
};

// Helper function to convert HEX color to HSL
const hexToHSL = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return [h, s, l];
}

// Helper function to find a color that is dissimilar to excluded colors
const findDissimilarColor = (availableColors, excludedColorsHSL) => {
  const maxAttempts = 100; // Max number of attempts to find a dissimilar color

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const randomIndex = Math.floor(Math.random() * availableColors.length);
    const randomColor = availableColors[randomIndex];

    const randomColorHSL = hexToHSL(randomColor.color);

    // Check if the random color is dissimilar to all excluded colors
    const isDissimilar = excludedColorsHSL.every((excludedHSL) => {
      const hueDifference = Math.abs(randomColorHSL[0] - excludedHSL[0]);
      const saturationDifference = Math.abs(randomColorHSL[1] - excludedHSL[1]);
      const lightnessDifference = Math.abs(randomColorHSL[2] - excludedHSL[2]);

      return (
        hueDifference > 0.2 || // Adjust this threshold for hue dissimilarity
        saturationDifference > 0.2 || // Adjust this threshold for saturation dissimilarity
        lightnessDifference > 0.2 // Adjust this threshold for lightness dissimilarity
      );
    });

    if (isDissimilar) {
      return randomColor;
    }
  }

  // If no dissimilar color is found after maxAttempts, return null or handle it as needed.
  return null;
}

export function generateTaskBgVariationColor(taskColor) {
  const color = taskColor.startsWith('#') ? taskColor.slice(1) : taskColor;
  const [r, g, b] = [
    parseInt(color.slice(0, 2), 16),
    parseInt(color.slice(2, 4), 16),
    parseInt(color.slice(4, 6), 16)
  ];

  const [R, G, B] = [r, g, b].map(c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;

  if (luminance > 0.5) {
    const darkenFactor = 0.7;
    return `rgba(${Math.floor(r * darkenFactor)}, ${Math.floor(g * darkenFactor)}, ${Math.floor(b * darkenFactor)}, 0.9)`;
  }

  const lightenFactor = 0.8;
  return `rgba(${Math.min(255, Math.floor(r + (255 - r) * lightenFactor))}, ${Math.min(255, Math.floor(g + (255 - g) * lightenFactor))}, ${Math.min(255, Math.floor(b + (255 - b) * lightenFactor))}, 0.9)`;
}

export const taskRenderer = (record, globalTaskColor, globalTaskStyle, viewType, rowHeight = 60, barMargin = 10) => {
  const globalColor = globalTaskColor !== MIXED_TASK_COLOR ? globalTaskColor : "";
  const globalStyle = globalTaskStyle !== MIXED_TASK_STYLE ? globalTaskStyle : "";
  const backgroundColor = record.color || SWIVU_TASK_DEFAULT_COLOR
  const isStripStyle = record?.taskStyle ? record.taskStyle === STRIPED_TASK_STYLE : globalStyle === STRIPED_TASK_STYLE;
  const contrastColor = bgContrastHelper(globalColor || record.color, "#fff", "#000");
  const lineStylePermitsContrast = (lineStyle) => ![ 'line', 'dashed', 'minimal', 'hollow' ].includes(lineStyle);
  const canApplyBgContrast = isStripStyle || lineStylePermitsContrast(record.taskStyle || globalStyle);
  const color = canApplyBgContrast ? (contrastColor || "#000") : '';
  let style = `height: ${rowHeight - (viewType === ViewType.compact ? 0.5:
    viewType === ViewType.compressed ? 0.25: 2) * barMargin}px !important;`;
  style += isStripStyle ? `background: ${applyStripedBackground(globalColor || backgroundColor)};` : `color: ${globalColor || record.color};`;
  return `<div class="b-sch-horizontal flex flex-1">
    <div
      class="b-sch-event-wrap b-sch-swivu-task-style flex-1 ${!isStripStyle ? `b-sch-style-${record.taskStyle || globalStyle || "plain"} b-sch-custom-color` : "swivu-time-event"} swivu-unplanned-task static"
    >
      <div class="b-sch-event b-has-content" role="presentation" style="${style}">
        <div
          class="b-sch-event-content ${!canApplyBgContrast ? 'theme-text-color' : ''}"
          style="color: ${color}; font-size: 1em; ${record.archived ? 'text-decoration: line-through;' : ''}"
        >
          ${record.name}
        </div>
      </div>
    </div>
  </div>`;
}
